import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useProfile } from "@/hooks/useProfile";
import moreEnergyImg from "@/assets/more-energy-focus.png";
import betterHealthImg from "@/assets/better-health-longevity.png";
import improvedMoodImg from "@/assets/improved-mood-confidence.png";
// Female illustrations - user needs to add these image files to assets folder
import moreEnergyImgFemale from "@/assets/more-energy-focus-female.png";
import betterHealthImgFemale from "@/assets/better-health-longevity-female.png";
import improvedMoodImgFemale from "@/assets/improved-mood-confidence-female.png";

export const EducationalSlides = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showLoading, setShowLoading] = useState(false);
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { t } = useTranslation();
  
  const isFemale = profile?.gender === 'female';
  
  const slides = useMemo(() => [
    {
      title: t('educationalSlides.slide1.title'),
      description: t('educationalSlides.slide1.description'),
      image: isFemale ? moreEnergyImgFemale : moreEnergyImg,
    },
    {
      title: t('educationalSlides.slide2.title'),
      description: t('educationalSlides.slide2.description'),
      image: isFemale ? betterHealthImgFemale : betterHealthImg,
    },
    {
      title: t('educationalSlides.slide3.title'),
      description: t('educationalSlides.slide3.description'),
      image: isFemale ? improvedMoodImgFemale : improvedMoodImg,
    },
  ], [isFemale, t]);

  useEffect(() => {
    if (showLoading) {
      const timer = setTimeout(() => {
        // Force tutorial to show by setting localStorage flag
        localStorage.setItem('force_tutorial', 'true');
        navigate("/dashboard");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showLoading, navigate]);

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      setShowLoading(true);
    }
  };

  if (showLoading) {
    return (
      <div className="min-h-screen bg-gradient-bg flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4">
          <LoadingSpinner size="lg" />
          <p className="text-foreground text-lg">{t('educationalSlides.preparingTutorial')}</p>
        </div>
      </div>
    );
  }

  const slide = slides[currentSlide];

  return (
    <div className="min-h-screen bg-gradient-bg flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-card rounded-2xl shadow-lg p-8 sm:p-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              {t('educationalSlides.header')}
            </h2>
            <p className="text-muted-foreground">
              {t('educationalSlides.subheader')}
            </p>
          </div>

          <div className="flex flex-col items-center gap-6 mb-8">
            <div className="w-full max-w-md sm:max-w-xs aspect-square flex items-center justify-center" style={{ transform: 'translateY(-0.5cm)' }}>
              <img 
                src={slide.image} 
                alt={slide.title}
                className="w-full h-full object-contain"
              />
            </div>
            
            <div className="text-center">
              <h3 className="text-xl sm:text-2xl font-semibold text-foreground mb-3">
                {slide.title}
              </h3>
              <p className="text-muted-foreground text-lg">
                {slide.description}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {slides.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 rounded-full transition-all ${
                    index === currentSlide
                      ? "w-8 bg-primary"
                      : "w-2 bg-muted"
                  }`}
                />
              ))}
            </div>
            
            <Button
              onClick={handleNext}
              className="bg-gradient-primary text-black shadow-glow hover:shadow-lg transition-all"
            >
              {currentSlide < slides.length - 1 ? (
                <>
                  {t('common.next')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </>
              ) : (
                t('educationalSlides.getStarted')
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
